import React from "react";
import { Link } from "react-router-dom";
import { Column } from "../components/Layout";
import { Signature } from "../components/Signature";
import { FadeIn } from "../components/FadeIn";
import { OutlineLink } from "../components/OutlineLink";
import { Seo } from "../components/Seo";
import { AuthorSignature } from "../components/AuthorSignature";

const HERO_IMAGES = [
  {
    src: "https://static.prod-images.emergentagent.com/jobs/52749592-c014-402b-8984-74e3e9024749/images/7684babc9ce0943fd0b123a33d3b1e1c8c6ef232ae4489daeeacb00795bd0e9a.jpeg",
    alt: "Esther Mokoena, A Life in Full: the aged-pewter feather quill on the linen cover, in close",
  },
  {
    src: "https://static.prod-images.emergentagent.com/jobs/52749592-c014-402b-8984-74e3e9024749/images/b570b3acdf5bbb9152adc2a3d87776ecb9c53b06a09dae3e4b23ef1d8aa90700.jpeg",
    alt: "The bound Life Study standing, a museum-grade hardcover",
  },
  {
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_quill-study/artifacts/db2z1dpk_image.webp",
    alt: "An open Life Study spread: a biography page beside a family tree",
  },
  {
    src: "https://static.prod-images.emergentagent.com/jobs/52749592-c014-402b-8984-74e3e9024749/images/3cd87c70071f473cab694e75e95f21945d85b6c9445fcfcca6d2daaea718e801.jpeg",
    alt: "A stack of Esther Mokoena, A Life in Full: the sewn hardcover volumes of a heritage run",
  },
  {
    src: "https://static.prod-images.emergentagent.com/jobs/52749592-c014-402b-8984-74e3e9024749/images/bea46ada8390f7312ed6a07a2e4005998e85ad854b96fc63b0e4c2e2b009d7d5.jpeg",
    alt: "Esther Mokoena, A Life in Full, spine out on a shelf among bound volumes",
  },
  {
    src: "https://static.prod-images.emergentagent.com/jobs/52749592-c014-402b-8984-74e3e9024749/images/7b1a151ee2a4a76d559527526d5eaf66d2044569bbd5d4bbef82e55ae7b58b9e.jpeg",
    alt: "Esther Mokoena, A Life in Full: the finished hardcover resting on a table",
  },
];

const HeroCarousel = () => {
  const scroller = React.useRef(null);
  const [active, setActive] = React.useState(0);
  const go = (i) => {
    const el = scroller.current;
    if (!el) return;
    const child = el.children[i];
    if (child) el.scrollTo({ left: child.offsetLeft, behavior: "smooth" });
  };
  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    setActive(Math.round(el.scrollLeft / el.clientWidth));
  };
  return (
    <div className="relative mx-auto max-w-[360px] sm:max-w-[420px] lg:max-w-none" data-testid="hero-carousel">
      <div className="border border-[#7A2E2E] p-2 sm:p-3 bg-[#F4F1EA]">
        <div
          ref={scroller}
          onScroll={onScroll}
          className="flex overflow-x-auto snap-x snap-mandatory [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none" }}
        >
          {HERO_IMAGES.map((im, i) => (
            <div key={i} className="snap-center shrink-0 w-full flex items-center justify-center">
              <img
                src={im.src}
                alt={im.alt}
                className="w-full h-[420px] sm:h-[520px] object-contain"
                loading={i === 0 ? "eager" : "lazy"}
                data-testid={`hero-slide-${i}`}
              />
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(Math.max(0, active - 1))}
        aria-label="Previous image"
        data-testid="hero-prev"
        className={`absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 flex items-center justify-center bg-[#F4F1EA]/85 text-[#7A2E2E] text-[18px] leading-none transition-opacity hover:bg-[#F4F1EA] ${active === 0 ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      >
        &#8249;
      </button>
      <button
        type="button"
        onClick={() => go(Math.min(HERO_IMAGES.length - 1, active + 1))}
        aria-label="Next image"
        data-testid="hero-next"
        className={`absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 flex items-center justify-center bg-[#F4F1EA]/85 text-[#7A2E2E] text-[18px] leading-none transition-opacity hover:bg-[#F4F1EA] ${active === HERO_IMAGES.length - 1 ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      >
        &#8250;
      </button>

      <div className="mt-4 flex justify-center gap-2" data-testid="hero-dots">
        {HERO_IMAGES.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => go(i)}
            aria-label={`Go to image ${i + 1}`}
            data-testid={`hero-dot-${i}`}
            className={`h-1.5 rounded-full transition-all duration-300 ${active === i ? "w-6 bg-[#7A2E2E]" : "w-1.5 bg-[#A8A296] hover:bg-[#7A2E2E]"}`}
          />
        ))}
      </div>

      <p
        className="mt-5 text-center text-[11px] uppercase tracking-[0.32em] text-[#7A2E2E]"
        data-testid="hero-caption"
      >
        A specimen of a complete Life Study
      </p>
    </div>
  );
};

const PORTRAIT_IMAGE =
  "https://customer-assets-jai6qajn.emergentagent.net/job_quill-study/artifacts/654e72ad48a2b22a_image.png";

const SPECIMEN_IMAGE =
  "https://customer-assets.emergentagent.com/job_quill-study/artifacts/ch6ar9r0_image.png";

const Home = () => {
  return (
    <div data-testid="home-page">
      <Seo
        title="The Raven's Quill · Anthropological Biography & Bespoke Legacy Preservation, South Africa"
        description="A fine press producing museum-grade anthropological biographies of extraordinary lives. We do not ghostwrite legacies. We document them from the inside."
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "The Raven's Quill",
            description:
              "A fine press producing museum-grade anthropological biographies of extraordinary lives. We do not ghostwrite legacies. We document them from the inside.",
            url: "https://theravensquill.co.za",
            email: "info@theravensquill.co.za",
            telephone: "+27 79 281 8435",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Pretoria",
              addressRegion: "Gauteng",
              addressCountry: "South Africa",
            },
            founder: { "@type": "Person", name: "Zukiswa Phoofolo" },
          },
          {
            "@context": "https://schema.org",
            "@type": "Person",
            name: "Zukiswa Phoofolo",
            jobTitle: "Writer-Anthropologist",
            worksFor: { "@type": "Organization", name: "The Raven's Quill" },
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "The Life Study",
            description: "A year-long anthropological biography commission.",
            provider: { "@type": "Organization", name: "The Raven's Quill" },
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "The Final Tribute",
            description: "A seventy-two-hour funeral tribute and obituary commission.",
            provider: { "@type": "Organization", name: "The Raven's Quill" },
          },
        ]}
      />

      {/* HERO */}
      <section className="pt-10 pb-20 md:pt-16 md:pb-28" data-testid="hero-section">
        <div className="mx-auto max-w-[1160px] px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <FadeIn delay={0.1} className="order-2 lg:order-1" data-testid="hero-image-section">
              <HeroCarousel />
            </FadeIn>

            <div className="order-1 lg:order-2">
              <FadeIn>
                <h1 className="max-w-[560px] text-[34px] leading-[1.25] sm:text-[40px] md:text-[46px] md:leading-[1.2] italic text-[#1C1A17]" data-testid="hero-headline">
                  We do not ghostwrite legacies. We document them from the inside.
                </h1>
              </FadeIn>
              <FadeIn delay={0.15}>
                <p className="mt-10 max-w-[540px] text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
                  An elite anthropological engagement that transforms an extraordinary
                  life into a permanent, museum-grade literary work. Conducted for
                  corporate pioneers, institutional builders, and prominent elders, by a
                  writer-anthropologist who comes, and stays, and writes.
                </p>
              </FadeIn>
              <FadeIn delay={0.3}>
                <div className="mt-12 flex flex-col sm:flex-row gap-4 sm:gap-5">
                  <OutlineLink to="/commission" testid="hero-cta-consultation">
                    Request a Private Consultation
                  </OutlineLink>
                  <OutlineLink to="/the-method" variant="oxblood" testid="hero-cta-method">
                    Read the Method
                  </OutlineLink>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* THE PHILOSOPHY */}
      <section className="py-20 md:py-28">
        <Column>
          <Signature
            label="The Philosophy"
            title="Writing with you. Not for you."
            testid="philosophy-signature"
          />
          <div className="space-y-7 text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
            <FadeIn as="p">
              A conventional biography extracts your story, disappears into a room,
              and returns a manuscript that pretends the writer was never there. The
              Raven&rsquo;s Quill rejects that illusion.
            </FadeIn>
            <FadeIn as="p">
              The practice is led by a writer-anthropologist who applies rigorous
              ethnographic method to capture a life in full context. Two distinct
              presences sit on the page: your own voice, and the analytical lens of an
              observer who has stood close to your world. The result is a
              collaborative, co-interpreted work, honest, deeply textured, and
              structurally uncompromising.
            </FadeIn>
            <FadeIn as="p">
              It is not a memoir you commission about yourself. It is a study,
              authored about you, by someone qualified to judge a life significant
              enough to be studied. That distinction is the whole of it.
            </FadeIn>
          </div>
        </Column>
      </section>

      {/* TWO MOMENTS */}
      <section className="py-20 md:py-28" data-testid="two-moments-section">
        <Column>
          <Signature label="Two Moments" testid="two-moments-signature" />
          <div className="space-y-7 text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
            <FadeIn as="p">
              There are two moments in which a human life most asks to be written.
            </FadeIn>
            <FadeIn as="p">
              The first is while it can still be studied: when memories remain living,
              photographs still invite questions, and those who shaped a life can still
              be heard in conversation. This is The Life Study, a year-long
              anthropological commission, bound as an heirloom for the generations who
              follow.
            </FadeIn>
            <FadeIn as="p">
              The second is at the moment of farewell, when a family must find words
              equal to the weight of a life. This is The Final Tribute, a
              seventy-two-hour commission undertaken in the days between death and
              farewell.
            </FadeIn>
          </div>
          <FadeIn className="mt-12 flex flex-col sm:flex-row gap-4 sm:gap-5">
            <OutlineLink to="/the-method" testid="two-moments-life-study">
              The Life Study
            </OutlineLink>
            <OutlineLink to="/final-tribute" variant="oxblood" testid="two-moments-final-tribute">
              The Final Tribute
            </OutlineLink>
          </FadeIn>
          <FadeIn className="mt-10 flex flex-col sm:flex-row gap-4 sm:gap-10">
            <Link
              to="/from-the-page"
              data-testid="home-from-the-page-link"
              className="text-[18px] md:text-[20px] italic text-[#1C1A17] underline underline-offset-[6px] decoration-[#7A2E2E] hover:text-[#7A2E2E] transition-colors"
            >
              Read a Life Study Sample
            </Link>
            <Link
              to="/from-the-booklet"
              data-testid="home-from-the-booklet-link"
              className="text-[18px] md:text-[20px] italic text-[#1C1A17] underline underline-offset-[6px] decoration-[#7A2E2E] hover:text-[#7A2E2E] transition-colors"
            >
              Read a Final Tribute Sample
            </Link>
          </FadeIn>
        </Column>
      </section>

      {/* THE ANTHROPOLOGIST */}
      <section className="py-20 md:py-28" data-testid="anthropologist-teaser">
        <div className="mx-auto max-w-[1160px] px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-1 lg:order-1">
              <FadeIn>
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#7A2E2E]">The Anthropologist</p>
                <hr className="mt-5 mb-0 border-0" style={{ width: "72px", height: "0.75px", backgroundColor: "#7A2E2E" }} />
              </FadeIn>
              <FadeIn delay={0.1} className="lg:hidden mt-8" data-testid="anthropologist-image-mobile">
                <img
                  src={PORTRAIT_IMAGE}
                  alt="Zukiswa Phoofolo, writer-anthropologist and founder of The Raven's Quill"
                  className="w-full h-auto mx-auto max-w-[360px] sm:max-w-[420px] border border-[#7A2E2E] p-2 sm:p-3"
                  loading="lazy"
                />
              </FadeIn>
              <FadeIn delay={0.1}>
                <p className="mt-8 text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
                  The Raven&rsquo;s Quill is the work of Zukiswa Phoofolo, a
                  writer-anthropologist who studies a single life the way others study a
                  system and a culture: from the inside, over time, and in the company of the people
                  who shaped it. She has spent seventeen years in early childhood development
                  across the Global South, attending to how meaning is formed and handed
                  on; she now also preserves lives in their fullness, when their shape is
                  complete.
                </p>
              </FadeIn>
              <FadeIn delay={0.15}>
                <AuthorSignature className="mt-9" scale={0.8} />
              </FadeIn>
              <FadeIn delay={0.2}>
                <div className="mt-9">
                  <OutlineLink to="/the-anthropologist" variant="oxblood" testid="anthropologist-cta">
                    Meet the Anthropologist
                  </OutlineLink>
                </div>
              </FadeIn>
            </div>

            <FadeIn delay={0.1} className="hidden lg:block order-2 lg:order-2" data-testid="anthropologist-image">
              <img
                src={PORTRAIT_IMAGE}
                alt="Zukiswa Phoofolo, writer-anthropologist and founder of The Raven's Quill"
                className="w-full h-auto mx-auto max-w-[360px] sm:max-w-[420px] lg:max-w-none border border-[#7A2E2E] p-2 sm:p-3"
                loading="lazy"
              />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="py-20 md:py-28">
        <Column>
          <Signature label="Who This Is For" testid="who-signature" />
          <FadeIn as="p" className="text-[19px] md:text-[21px] italic leading-[1.6] text-[#1C1A17]">
            The Raven&rsquo;s Quill undertakes only a few Life Studies at a time, for lives
            that carry weight.
          </FadeIn>

          <div className="mt-14 space-y-12">
            <FadeIn>
              <p className="text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
                <span className="font-bold">Community and political elders,</span> whose
                paths run through liberation, exile, or the building of institutions,
                and whose stories deserve a record equal to them.
              </p>
            </FadeIn>
            <FadeIn>
              <p className="text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
                <span className="font-bold">Corporate pioneers and board leaders,</span>{" "}
                stepping back from executive life, who want a critical and reflective
                account of what they built rather than a public-relations memoir.
              </p>
            </FadeIn>
            <FadeIn>
              <p className="text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
                <span className="font-bold">Matriarchs and patriarchs,</span> whose
                families seek a durable, beautifully bound record that holds the
                texture of an entire lineage for the generations who follow.
              </p>
            </FadeIn>
          </div>
        </Column>
      </section>

      {/* THE WORK */}
      <section className="py-20 md:py-28">
        <Column>
          <Signature label="The Work" testid="home-work-signature" />
          <FadeIn as="p" className="text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
            What is delivered is not a manuscript but an heirloom: a limited run of
            sewn, hardcover volumes, bound to museum standard, with restored
            photographs woven into the narrative, and a press-ready master held by the
            family permanently.
          </FadeIn>
          <FadeIn className="mt-12">
            <OutlineLink to="/the-work" testid="home-work-cta">
              See What Is Delivered
            </OutlineLink>
          </FadeIn>
        </Column>
      </section>

      {/* CLOSING LINE (quiet full-width moment, on paper) */}
      <section className="py-24 md:py-32" data-testid="home-closing">
        <Column>
          <FadeIn>
            <hr className="mx-auto border-0" style={{ width: "72px", height: "0.75px", backgroundColor: "#7A2E2E" }} />
            <p className="mt-12 text-center text-[24px] md:text-[30px] italic leading-[1.5] text-[#1C1A17]">
              Some lives are too large for a timeline. They ask to be understood, not
              merely recorded.
            </p>
          </FadeIn>
        </Column>
      </section>
    </div>
  );
};

export default Home;
